import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    Card,
    CardContent,
    Grid,
    TextField,
    Select,
    MenuItem,
    FormControl,
    Checkbox
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { PathList } from "../../Common/Routes/Path";
import AxiosInstance from "../../Common/Api/ApiHelper";
import {apiList} from "../../Common/Api/ApiList";
import {toast} from "react-toastify";
import ErrorToaster from "../../Component/ErrorToaster";
import ArrowBackIosRoundedIcon from "@mui/icons-material/ArrowBackIosRounded";

const PlansEditScreen = () => {
    const navigate = useNavigate();

    const { id } = useParams();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [charge, setCharge] = useState();

    const initial = {
        name: "",
        price: "",
        currency: "usd",
        billing_cycle: "month",
        status: "active",
        description: "",
        service_fee_percentage: "",
        priority_booking: false,
        // waitlist_jump_priority: false,
        // xp_multiplier: "",
        // badge_tier: "",
        // free_xp_boost: false,
        // monthly_promo_credits: "",
        // bonus_reward_sessions: "",
        // free_sessions_per_month: "",
        // analytics_access: "",
        // device_integration: "",
        // social_achievements: false,
        support_level: "",
        // early_access: false,
        // special_event_invites: false,
        // exclusive_challenges_access: false,
        plan_type: ""
    };

    const [planData, setPlanData] = useState(initial);

    const [errors, setErrors] = useState({
        name: "",
        price: "",
        plan_type: "",
    });

    // Fetch plan data
    useEffect(() => {
        const fetchPlan = async () => {
            try {
                const response = await AxiosInstance.get(`${apiList.GetPlanById}?id=${id}`);
                if (response.data.s === 1) {
                    const data = response.data.r;

                    setPlanData({
                        ...data,
                        priority_booking: Number(data.priority_booking) === 1,
                        // waitlist_jump_priority: Number(data.waitlist_jump_priority) === 1,
                        // free_xp_boost: Number(data.free_xp_boost) === 1,
                        // social_achievements: Number(data.social_achievements) === 1,
                        // early_access: Number(data.early_access) === 1,
                        // special_event_invites: Number(data.special_event_invites) === 1,
                        // exclusive_challenges_access: Number(data.exclusive_challenges_access) === 1,
                    });
                } else {
                    toast.error(response.data.m || "Failed to fetch plan");
                }
            } catch (error) {
                ErrorToaster(error);
            }
        };

        if (id) fetchPlan();
    }, [id]);

    // on input & select box changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setPlanData({ ...planData, [name]: value });
    };

    // on check box changes
    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setPlanData({ ...planData, [name]: checked });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;

        const newErrors = {};

        if (!planData.name) newErrors.name = "Plan Name is required";
        if (!planData.price) newErrors.price = "Price is required";
        if (!planData.plan_type) newErrors.plan_type = "Plan Type is required";

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        } else {
            setErrors({});
        }

        setIsSubmitting(true);
        try {
            const response = await AxiosInstance.post(apiList?.UpdatePlan, planData);
            if (response.data.s === 1) {
                toast.success("Plan updated successfully");

                setTimeout(() => {
                    navigate(PathList.Plans);
                }, 1000);
            } else {
                toast.error(response.data.m);
            }
        } catch (error) {
            ErrorToaster(error);
        }
        finally {
            setIsSubmitting(false);
        }
    }

    useEffect(() => {
        const fetchCharge = async () => {
            try {
                const response = await AxiosInstance.get(`${apiList.getStripeCharge}`);
                if (response.data.s === 1) {
                    const data = response.data.r.charge;
                    setCharge(data)
                } else {
                    toast.error(response.data.m || "Failed to fetchCharge");
                }
            } catch (error) {
                ErrorToaster(error);
            }
        };

        fetchCharge();
    }, []);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5, // adjust spacing as needed
                    pb: 2,
                }}
            >
                <ArrowBackIosRoundedIcon
                    sx={{
                        cursor: "pointer",
                        "&:hover": {
                            color: "#3C75D4",
                        },
                    }}
                    onClick={() => navigate(PathList.Plans)}
                />
                <Typography variant="h6" sx={{ fontSize: "28px" }}>
                    Edit Plan
                </Typography>
            </Box>

            {/* Form Card */}
            <Card sx={{ mt: 3, p: 2 }}>
                <CardContent>
                    <form style={{width: "100%"}} onSubmit={handleSubmit}>
                        <Grid container spacing={3}>
                            {/* Plan Name */}
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Name</Typography>
                                <TextField
                                    name="name"
                                    placeholder="Enter plan name"
                                    fullWidth
                                    value={planData.name}
                                    onChange={handleChange}
                                />
                                {errors.name && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.name}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Price */}
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Price</Typography>
                                <TextField
                                    name="price"
                                    placeholder="Enter price"
                                    fullWidth
                                    value={planData.price}
                                    onChange={handleChange}
                                />
                                {errors.price && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.price}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Currency */}
                            <Grid item xs={12} md={4}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Currency</Typography>
                                <TextField
                                    name="currency"
                                    value={planData.currency}
                                    fullWidth
                                    aria-readonly={true}
                                />
                            </Grid>

                            {/* Billing Cycle */}
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Billing Cycle</Typography>
                                    <Select
                                        name="billing_cycle"
                                        value={planData.billing_cycle}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Billing Cycle
                                        </MenuItem>
                                        <MenuItem value="month">Monthly</MenuItem>
                                        <MenuItem value="year">Yearly</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Status */}
                            <Grid item xs={12} md={4}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Status</Typography>
                                    <Select
                                        name="status"
                                        value={planData.status}
                                        onChange={handleChange}
                                    >
                                        <MenuItem value="" disabled>
                                            Select Status
                                        </MenuItem>
                                        <MenuItem value="active">Active</MenuItem>
                                        <MenuItem value="in-active">In-Active</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item md={4} xs={12}>
                                <FormControl fullWidth size="small">
                                    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Plan Type</Typography>
                                    <Select
                                        name="plan_type"
                                        value={planData.plan_type}
                                        onChange={handleChange}
                                        displayEmpty
                                    >
                                        <MenuItem value="" disabled>Select</MenuItem>
                                        <MenuItem value="1">Standard</MenuItem>
                                        <MenuItem value="2">Plus</MenuItem>
                                        <MenuItem value="3">Elite</MenuItem>
                                    </Select>
                                </FormControl>

                                {errors.plan_type && (
                                    <Typography sx={{ color: "red", fontSize: "14px" }}>
                                        {errors.plan_type}
                                    </Typography>
                                )}
                            </Grid>

                            {/* Description (CKEditor) */}
                            <Grid item md={12}>
                                <Typography sx={{mb: 1, fontSize: "16px", fontWeight: 500}}>
                                    Description
                                </Typography>
                                <textarea name="description" placeholder="Description" cols="5" rows="5"
                                          style={{
                                              width: "100%",
                                              minHeight: "150px",
                                              paddingTop: "15px",
                                              borderRadius: "10px",
                                              border: "1px solid #c4c4c4",
                                              paddingLeft: "15px",
                                              fontSize: "16px",
                                              outlineColor: "none"
                                          }}
                                          value={planData.description}
                                          onChange={handleChange}
                                ></textarea>
                            </Grid>

                            <Grid item md={12}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>-- Booking & Fees --</Typography>

                                <Grid container spacing={4} alignItems="center">
                                    {/* Service Fee */}
                                    <Grid item md={4} xs={12}>
                                        <Typography sx={{fontSize: "16px", fontWeight: "500"}}>
                                            Discount Percentage Fee
                                        </Typography>
                                        <TextField
                                            name="service_fee_percentage"
                                            value={planData.service_fee_percentage}
                                            onChange={handleChange}
                                            fullWidth
                                            placeholder="%"
                                        />
                                        <Typography sx={{fontSize: "13px"}}>
                                            {charge} (Up to this maximum fee; higher values are not applicable)
                                        </Typography>
                                    </Grid>

                                    {/* Priority Booking */}
                                    <Grid item md={4} xs={12}>
                                        <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>
                                            Priority Booking
                                        </Typography>
                                        <Checkbox name="priority_booking"
                                                  checked={planData.priority_booking}
                                                  onChange={handleCheckboxChange}
                                                  sx={{
                                                      color: "#c4c4bbfa",
                                                      "&.Mui-checked": {
                                                          color: "#1976d2"
                                                      },
                                                  }}
                                        />
                                    </Grid>

                                    {/* Waitlist Jump Booking */}
                                    {/*<Grid item md={4} xs={12}>*/}
                                    {/*    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                                    {/*        Waitlist Jump Priority*/}
                                    {/*    </Typography>*/}
                                    {/*    <Checkbox name="waitlist_jump_priority"*/}
                                    {/*              checked={planData.waitlist_jump_priority}*/}
                                    {/*              onChange={handleCheckboxChange}*/}
                                    {/*              sx={{*/}
                                    {/*                  color: "#c4c4bbfa",*/}
                                    {/*                  "&.Mui-checked": {*/}
                                    {/*                      color: "#1976d2"*/}
                                    {/*                  },*/}
                                    {/*              }}*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Grid>

                            {/*<Grid item md={12}>*/}
                            {/*    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>-- Gamification --</Typography>*/}

                            {/*    <Grid container spacing={4} alignItems="center">*/}
                            {/*        /!* XP Multiplier *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <FormControl fullWidth size="small">*/}
                            {/*                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>XP Multiplier</Typography>*/}
                            {/*                <Select*/}
                            {/*                    name="xp_multiplier"*/}
                            {/*                    value={planData.xp_multiplier}*/}
                            {/*                    onChange={handleChange}*/}
                            {/*                    displayEmpty*/}
                            {/*                >*/}
                            {/*                    <MenuItem value="" disabled>Select</MenuItem>*/}
                            {/*                    <MenuItem value="1x">1x</MenuItem>*/}
                            {/*                    <MenuItem value="2x">2x</MenuItem>*/}
                            {/*                    <MenuItem value="3x">3x</MenuItem>*/}
                            {/*                </Select>*/}
                            {/*            </FormControl>*/}
                            {/*        </Grid>*/}

                            {/*        /!* Badge Tier  *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <FormControl fullWidth size="small">*/}
                            {/*                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Badge Tier</Typography>*/}
                            {/*                <Select*/}
                            {/*                    name="badge_tier"*/}
                            {/*                    value={planData.badge_tier}*/}
                            {/*                    onChange={handleChange}*/}
                            {/*                    displayEmpty*/}
                            {/*                >*/}
                            {/*                    <MenuItem value="" disabled>Select</MenuItem>*/}
                            {/*                    <MenuItem value="standard">Standard</MenuItem>*/}
                            {/*                    <MenuItem value="plus">Plus</MenuItem>*/}
                            {/*                    <MenuItem value="elite">Elite</MenuItem>*/}
                            {/*                </Select>*/}
                            {/*            </FormControl>*/}
                            {/*        </Grid>*/}

                            {/*        /!* Free XP Boost *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                            {/*                Free XP Boost*/}
                            {/*            </Typography>*/}
                            {/*            <Checkbox name="free_xp_boost"*/}
                            {/*                      checked={planData.free_xp_boost}*/}
                            {/*                      onChange={handleCheckboxChange}*/}
                            {/*                      sx={{*/}
                            {/*                          color: "#c4c4bbfa",*/}
                            {/*                          "&.Mui-checked": {*/}
                            {/*                              color: "#1976d2"*/}
                            {/*                          },*/}
                            {/*                      }}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}
                            {/*    </Grid>*/}
                            {/*</Grid>*/}

                            {/*<Grid item md={12}>*/}
                            {/*    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>-- Rewards & Credits --</Typography>*/}

                            {/*    <Grid container spacing={4} alignItems="center">*/}
                            {/*         Monthly Promo Credits */}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                            {/*                Monthly Promo Credits*/}
                            {/*            </Typography>*/}
                            {/*            <TextField*/}
                            {/*                name="monthly_promo_credits"*/}
                            {/*                fullWidth*/}
                            {/*                placeholder="$"*/}
                            {/*                value={planData.monthly_promo_credits}*/}
                            {/*                onChange={handleChange}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}

                            {/*         Bonus Reward Sessions */}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                            {/*                Bonus Reward Sessions*/}
                            {/*            </Typography>*/}
                            {/*            <TextField*/}
                            {/*                name="bonus_reward_sessions"*/}
                            {/*                fullWidth*/}
                            {/*                placeholder=""*/}
                            {/*                value={planData.bonus_reward_sessions}*/}
                            {/*                onChange={handleChange}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}

                            {/*        /!* Max Sessions/Month  *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                            {/*                Free Sessions/Month*/}
                            {/*            </Typography>*/}
                            {/*            <TextField*/}
                            {/*                name="free_sessions_per_month"*/}
                            {/*                fullWidth*/}
                            {/*                placeholder=""*/}
                            {/*                value={planData.free_sessions_per_month}*/}
                            {/*                onChange={handleChange}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}
                            {/*    </Grid>*/}
                            {/*</Grid>*/}

                            {/*<Grid item md={12}>*/}
                            {/*    <Typography sx={{fontSize: "18px", fontWeight: "500"}}>-- Analytics & Integration --</Typography>*/}

                            {/*    <Grid container spacing={4} alignItems="center">*/}
                            {/*         Analytics Access */}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <FormControl fullWidth size="small">*/}
                            {/*                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Analytics Access</Typography>*/}
                            {/*                <Select*/}
                            {/*                    name="analytics_access"*/}
                            {/*                    value={planData.analytics_access}*/}
                            {/*                    onChange={handleChange}*/}
                            {/*                    displayEmpty*/}
                            {/*                >*/}
                            {/*                    <MenuItem value="" disabled>Select</MenuItem>*/}
                            {/*                    <MenuItem value="none">None</MenuItem>*/}
                            {/*                    <MenuItem value="lite">Lite</MenuItem>*/}
                            {/*                    <MenuItem value="full">Full</MenuItem>*/}
                            {/*                </Select>*/}
                            {/*            </FormControl>*/}
                            {/*        </Grid>*/}

                            {/*        /!* Device Integration *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <FormControl fullWidth size="small">*/}
                            {/*                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Device Integration</Typography>*/}
                            {/*                <Select*/}
                            {/*                    name="device_integration"*/}
                            {/*                    value={planData.device_integration}*/}
                            {/*                    onChange={handleChange}*/}
                            {/*                    displayEmpty*/}
                            {/*                >*/}
                            {/*                    <MenuItem value="" disabled>Select</MenuItem>*/}
                            {/*                    <MenuItem value="none">None</MenuItem>*/}
                            {/*                    <MenuItem value="lite">Lite</MenuItem>*/}
                            {/*                    <MenuItem value="full">Full</MenuItem>*/}
                            {/*                </Select>*/}
                            {/*            </FormControl>*/}
                            {/*        </Grid>*/}

                            {/*        /!* Social Achievements *!/*/}
                            {/*        <Grid item md={4} xs={12}>*/}
                            {/*            <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                            {/*                Social Achievements*/}
                            {/*            </Typography>*/}
                            {/*            <Checkbox name="social_achievements"*/}
                            {/*                      checked={planData.social_achievements}*/}
                            {/*                      onChange={handleCheckboxChange}*/}
                            {/*                      sx={{*/}
                            {/*                          color: "#c4c4bbfa",*/}
                            {/*                          "&.Mui-checked": {*/}
                            {/*                              color: "#1976d2"*/}
                            {/*                          },*/}
                            {/*                      }}*/}
                            {/*            />*/}
                            {/*        </Grid>*/}
                            {/*    </Grid>*/}
                            {/*</Grid>*/}

                            <Grid item md={12}>
                                <Typography sx={{fontSize: "18px", fontWeight: "500"}}>-- Support & Perks --</Typography>

                                <Grid container spacing={4} alignItems="center">
                                    {/* Support Level */}
                                    <Grid item md={3} xs={12}>
                                        <FormControl fullWidth size="small">
                                            <Typography sx={{fontSize: "18px", fontWeight: "500"}}>Support Level</Typography>
                                            <Select
                                                name="support_level"
                                                value={planData.support_level}
                                                onChange={handleChange}
                                                displayEmpty
                                            >
                                                <MenuItem value="" disabled>Select</MenuItem>
                                                <MenuItem value="standard">Standard</MenuItem>
                                                <MenuItem value="priority">Priority</MenuItem>
                                                <MenuItem value="vip">VIP</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>

                                    {/* Early Access to Features */}
                                    {/*<Grid item md={3} xs={12}>*/}
                                    {/*    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                                    {/*        Early Access to Features*/}
                                    {/*    </Typography>*/}
                                    {/*    <Checkbox name="early_access"*/}
                                    {/*              checked={planData.early_access}*/}
                                    {/*              onChange={handleCheckboxChange}*/}
                                    {/*              sx={{*/}
                                    {/*                  color: "#c4c4bbfa",*/}
                                    {/*                  "&.Mui-checked": {*/}
                                    {/*                      color: "#1976d2"*/}
                                    {/*                  },*/}
                                    {/*              }}*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/* Special Event Invites */}
                                    {/*<Grid item md={3} xs={12}>*/}
                                    {/*    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                                    {/*        Special Event Invites*/}
                                    {/*    </Typography>*/}
                                    {/*    <Checkbox name="special_event_invites"*/}
                                    {/*              checked={planData.special_event_invites}*/}
                                    {/*              onChange={handleCheckboxChange}*/}
                                    {/*              sx={{*/}
                                    {/*                  color: "#c4c4bbfa",*/}
                                    {/*                  "&.Mui-checked": {*/}
                                    {/*                      color: "#1976d2"*/}
                                    {/*                  },*/}
                                    {/*              }}*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}

                                    {/* Exclusive Challenges Access */}
                                    {/*<Grid item md={3} xs={12}>*/}
                                    {/*    <Typography sx={{ fontSize: "16px", fontWeight: "500" }}>*/}
                                    {/*        Exclusive Challenges Access*/}
                                    {/*    </Typography>*/}
                                    {/*    <Checkbox name="exclusive_challenges_access"*/}
                                    {/*              checked={planData.exclusive_challenges_access}*/}
                                    {/*              onChange={handleCheckboxChange}*/}
                                    {/*              sx={{*/}
                                    {/*                  color: "#c4c4bbfa",*/}
                                    {/*                  "&.Mui-checked": {*/}
                                    {/*                      color: "#1976d2"*/}
                                    {/*                  },*/}
                                    {/*              }}*/}
                                    {/*    />*/}
                                    {/*</Grid>*/}
                                </Grid>
                            </Grid>

                            {/* Submit Button */}
                            <Grid item xs={12}>
                                <Box sx={{display: "flex", justifyContent: "flex-end", mt: 2}}>
                                    <Button
                                        variant="contained"
                                        disabled={isSubmitting}
                                        sx={{
                                            background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            color: "#fff",
                                            textTransform: "none",
                                            "&:hover": {
                                                background: "linear-gradient(181.45deg, #7AACFF -80.86%, rgba(60, 117, 213, 0.98) 98.77%)",
                                            },
                                        }}
                                        type="submit"
                                    >
                                        Save Plan
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </Card>
        </Box>
    );
};

export default PlansEditScreen;
